// frontend/src/components/Header.js - Themed version with professional design
import { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  Users, 
  ChevronDown, 
  LucideLogOut,
  Settings,
  User,
  Shield,
  Bell,
  Building,
  Users as UsersIcon,
  ChevronRight,
  Database,
  MessageSquare
} from 'lucide-react';
import axios from '../axiosConfig';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../contexts/ThemeContext';
import LanguageSelector from './LanguageSelector';
import { isSystemAdmin, isAdmin, isDriver } from '../utils/roleUtils';
import { getGlobalThreshold } from '../utils/preferences';
import { shouldShowNotificationMenu, shouldShowNotificationManagementMenu } from './notifications/notificationUtils';
import { usePreferences } from '../contexts/PreferenceContext';
import AppImage from './common/AppImage';
import MobileBottomNavigation from './navigation/MobileBottomNavigation';
import AnnouncementBell from './announcements/AnnouncementBell';
import logger from '../utils/logger';
import { resolveSelectedEntityLaunchPath } from '../utils/launchPagePaths';
import '../index.css';

function Header({ setToken, userRole, globalRouteRole, setUserRole, username, entities, currentEntity, setCurrentEntity, entityName, entityLogoUrl }) {
  const { t } = useTranslation();
  const { text, border, components, cn } = useTheme();
  const { systemSettings } = usePreferences();
  const navigate = useNavigate();
  const location = useLocation();
  const [isProfileDropdownOpen, setProfileDropdownOpen] = useState(false);
  const [isEntityDropdownOpen, setEntityDropdownOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredEntities, setFilteredEntities] = useState([]);
  const [showNotificationMenu, setShowNotificationMenu] = useState(false);
  const [showNotificationManagementMenu, setShowNotificationManagementMenu] = useState(false);
  const [showSurveyMenu, setShowSurveyMenu] = useState(false);
  const [showUserSurveyMenu, setShowUserSurveyMenu] = useState(false);
  const [showSurveyBadge, setShowSurveyBadge] = useState(false);
  const dropdownRef = useRef(null); //For ProfileDropdown
  const entityDropdownRef = useRef(null);
  // const [entityLogo, setEntityLogo] = useState(null);
  const globalThreshold = getGlobalThreshold();

  // Check notification menu visibility with the new API
  useEffect(() => {
    const checkNotificationVisibility = async () => {
      // Extract entityId from currentEntity (could be object or number)
      const entityId = typeof currentEntity === 'object' ? currentEntity?.entityId || currentEntity?.id : currentEntity;

      if (entityId) {
        // Check both user notification settings visibility and management visibility
        const [userMenuVisible, managementMenuVisible] = await Promise.all([
          shouldShowNotificationMenu(entityId),
          shouldShowNotificationManagementMenu(entityId, userRole)
        ]);

        setShowNotificationMenu(userMenuVisible);
        setShowNotificationManagementMenu(managementMenuVisible);
      } else {
        // No entity selected - hide both menus
        setShowNotificationMenu(false);
        setShowNotificationManagementMenu(false);
      }
    };

    checkNotificationVisibility();
  }, [currentEntity, systemSettings, userRole]);

  // Check survey management menu visibility (admin menu - based on role)
  useEffect(() => {
    const checkSurveyVisibility = async () => {
      // Extract entityId from currentEntity (could be object or number)
      const entityId = typeof currentEntity === 'object' ? currentEntity?.entityId || currentEntity?.id : currentEntity;

      if (entityId) {
        try {
          // Import the specific function for Survey Management menu (admin menu)
          const { shouldShowSurveyManagementMenu } = await import('./surveys/surveyUtils');
          const visible = await shouldShowSurveyManagementMenu(entityId);
          setShowSurveyMenu(visible);
        } catch (error) {
          logger.error('Error checking survey management menu visibility', error);
          setShowSurveyMenu(false);
        }
      } else {
        // No entity selected - hide survey menu
        setShowSurveyMenu(false);
      }
    };

    checkSurveyVisibility();
  }, [currentEntity, systemSettings, userRole]);

  // Check end user survey menu visibility (for taking surveys - no role checking)
  useEffect(() => {
    const checkUserSurveyVisibility = async () => {
      // Extract entityId from currentEntity (could be object or number)
      const entityId = typeof currentEntity === 'object' ? currentEntity?.entityId || currentEntity?.id : currentEntity;

      if (entityId) {
        try {
          // Import the specific function for end user surveys (no role checking)
          const { shouldShowSurveysForUser } = await import('./surveys/surveyUtils');
          const visible = await shouldShowSurveysForUser(entityId);
          setShowUserSurveyMenu(visible);
        } catch (error) {
          logger.error('Error checking end user survey menu visibility', error);
          setShowUserSurveyMenu(false);
        }
      } else {
        // No entity selected - hide user survey menu
        setShowUserSurveyMenu(false);
      }
    };

    checkUserSurveyVisibility();
  }, [currentEntity, systemSettings]);

  // Check survey badge status (system survey reminder) - DISABLED FOR NOW
/*   useEffect(() => {
    const checkSurveyBadgeStatus = async () => {
      // Extract entityId from currentEntity (could be object or number)
      const entityId = typeof currentEntity === 'object' ? currentEntity?.entityId || currentEntity?.id : currentEntity;

      if (entityId && showUserSurveyMenu) {
        try {
          const response = await axios.get(
            `/api/surveys/${entityId}/system-survey/badge-status`
          );

          if (response.data.success) {
            setShowSurveyBadge(response.data.showBadge);
          }
        } catch (error) {
          logger.error('Failed to load badge status', error);
        }
      } else {
        setShowSurveyBadge(false);
      }
    };

    checkSurveyBadgeStatus();
  }, [currentEntity, showUserSurveyMenu]); */

  // Handle survey click - dismiss badge when clicked
  const handleSurveyClick = async () => {
    setProfileDropdownOpen(false);

    // Dismiss badge when clicked
    if (showSurveyBadge) {
      const entityId = typeof currentEntity === 'object' ? currentEntity?.entityId || currentEntity?.id : currentEntity;
      try {
        await axios.post(
          `/api/surveys/${entityId}/system-survey/dismiss-badge`
        );
        setShowSurveyBadge(false);
      } catch (error) {
        logger.error('Failed to dismiss badge', error);
      }
    }
  };

  const handleLogout = async (event, reason = 'user_logout') => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    setProfileDropdownOpen(false);
    setEntityDropdownOpen(false);

    try {
      const token = localStorage.getItem('token');
      
      if (token) {
        // Call the logout endpoint
        await axios.post('/api/users/logout', 
          { reason }, 
          {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
        );
      }
    } catch (error) {
      logger.error('Logout error', error);
    } finally {
      // Always clear local state and redirect
      setToken(null);
      setUserRole(null);
      localStorage.clear();
      navigate('/login', { replace: true });
    }
    // Force a page reload. to fix the issue of the map not being reset when the user logs out and logs back in immediately 
    window.location.reload();
  };

  // Also add debug logs to handleEntitySelection
  const toggleProfileDropdown = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setProfileDropdownOpen((prev) => {
      if (!prev) {
        setEntityDropdownOpen(false); // Close entity dropdown if opening profile dropdown
      }
      return !prev;
    });
  };
  
  const toggleEntityDropdown = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setEntityDropdownOpen((prev) => {
      if (!prev) {
        setProfileDropdownOpen(false); // Close profile dropdown if opening entity dropdown
      }
      return !prev;
    });
  };

  // handleClickOutside
  const handleClickOutside = useCallback((event) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      setProfileDropdownOpen(false);
    }
    if (entityDropdownRef.current && !entityDropdownRef.current.contains(event.target)) {
      setEntityDropdownOpen(false);
    }
  }, [dropdownRef, entityDropdownRef]);

  useEffect(() => {
    if (isProfileDropdownOpen || isEntityDropdownOpen) {
      document.addEventListener('click', handleClickOutside);
    } else {
      document.removeEventListener('click', handleClickOutside);
    }

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [isProfileDropdownOpen, isEntityDropdownOpen, handleClickOutside]);

  useEffect(() => {
    setFilteredEntities(entities || []);
  }, [entities]);

  useEffect(() => {
    if (searchTerm) {
      const filtered = (entities || []).filter(entity => 
        entity.entityName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        entity.city?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        entity.province?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        entity.country?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredEntities(filtered);
    } else {
      setFilteredEntities(entities || []);
    }
  }, [searchTerm, entities]);

  // Group and sort entities by Country > Province > City, handling missing values
  const groupEntities = (entities) => {
    const sortedEntities = entities.sort((a, b) => {
      const countryComparison = (a.country || '').localeCompare(b.country || '');
      if (countryComparison !== 0) return countryComparison;

      const provinceComparison = (a.province || '').localeCompare(b.province || '');
      if (provinceComparison !== 0) return provinceComparison;

      const cityComparison = (a.city || '').localeCompare(b.city || '');
      if (cityComparison !== 0) return cityComparison;

      return (a.name || '').localeCompare(b.name || '');
    });

    // Group entities by Country > Province > City, but only include non-empty fields in the group key
    const grouped = sortedEntities.reduce((acc, entity) => {
      const locationParts = [];
      if (entity.country) locationParts.push(entity.country);
      if (entity.province) locationParts.push(entity.province);
      if (entity.city) locationParts.push(entity.city);

      const key = locationParts.length > 0 ? locationParts.join(' > ') : 'No Location Info';

      if (!acc[key]) acc[key] = [];
      acc[key].push(entity);
      return acc;
    }, {});

    return grouped;
  };

  // Only render the entity selector if the user has access to more than one entity
  const renderEntitySelector = () => {
    if (!entities || entities.length <= 1) return null;

    const currentEntityLabel = entityName || currentEntity?.entityName || currentEntity?.name || t('entity');
    const entitySelectorLabel = `${t('entity')}: ${currentEntityLabel}`;

    return (
      <div className="relative ml-4" ref={entityDropdownRef}>
        <button
          type="button"
          onClick={(e) => toggleEntityDropdown(e)}
          aria-label={entitySelectorLabel}
          title={entitySelectorLabel}
          className={cn(
            "flex items-center focus:outline-none transition-colors",
            "hover:text-blue-400 hover:underline"
          )}
        >
          <span className="sr-only">{entitySelectorLabel}</span>
          <ChevronDown className="w-4 h-4" />
        </button>
        {isEntityDropdownOpen && (
          <div className={cn(
            "absolute left-0 mt-2 w-80 rounded-md shadow-lg z-60",
            components.dropdown.base
          )}>
            {entities.length >= globalThreshold && (
              <input
                type="text"
                placeholder={t('searchEntities')}
                className={cn(
                  "w-full px-4 py-2 border-b transition-colors",
                  border.default,
                  components.input.base,
                  components.input.focus
                )}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            )}
            {renderEntityList()}
          </div>
        )}
      </div>
    );
  };

  const handleEntitySelection = (entity) => {
    setCurrentEntity(entity);  // This will trigger the `handleEntityChange` in App.js
    setEntityDropdownOpen(false);  // Close the dropdown after selection
    // Announcement projections own the selected audience and must reopen a
    // deep link (or clear a manager draft) in place when that audience changes.
    // App's role/concrete-entity guards still redirect an ineligible manager.
    // Other features retain the existing selected-entity launch behavior.
    if (!/^\/(?:announcements(?:\/[^/]+)?|entity-announcements|system-announcements)\/?$/.test(location.pathname)) {
      navigate(resolveSelectedEntityLaunchPath(entity, globalRouteRole));
    }
  };

  const renderEntityList = () => {
    const groupedEntities = groupEntities(filteredEntities);
  
    return (
      <div className="max-h-[80vh] min-w-[16rem] max-w-[24rem] w-auto overflow-y-auto">
        {/* "All" option */}
        <button
          className={cn(
            "w-full text-left px-4 py-2 transition-colors",
            components.dropdown.item
          )}
          onClick={() => handleEntitySelection({ id: 'all', name: t('All') })}
        >
          {t('All')} ({entities.length})
        </button>
  
        {/* Separator */}
        <div className={cn("border-b my-2", border.default)}></div>

        {/* Grouped and sorted entities */}
        {Object.keys(groupedEntities).map((group) => (
          <div key={group}>
            {group && (
              <div className={cn("px-4 py-1 font-semibold text-xs uppercase tracking-wider", text.muted)}>
                {group}
              </div>
            )}
            {groupedEntities[group].map((entity) => (
              <button
                key={entity.entityId}
                className={cn(
                  "w-full text-left pl-8 pr-4 py-2 transition-colors",
                  currentEntity?.entityId === entity.entityId 
                    ? components.dropdown.active
                    : components.dropdown.item
                )}
                onClick={() => handleEntitySelection(entity)}
              >
                {entity.entityName} {entity.status && entity.status !== 'active' && `(${entity.status})`}
              </button>
            ))}
          </div>
        ))}
      </div>
    );
  };

  
  return (
    <>
    <header className={cn(
      "fixed top-0 left-0 right-0 flex items-center justify-between px-4 py-2 z-20",
      "bg-gray-800 dark:bg-gray-900 text-white",
      "shadow-sm border-b border-gray-700 dark:border-gray-600"
    )} style={{ top: 'var(--service-availability-height, 0px)' }}>
      {/* Left side with logo and entity name */}
      <div className="flex min-w-0 items-center">
        {entityLogoUrl && (
          <AppImage
            src={entityLogoUrl}
            alt={t('entitylogo')}
            crossOrigin="anonymous"
            fallbackSrc={null}
            className="h-8 md:h-10 lg:h-12 w-auto mr-4 object-contain"
            style={{ maxWidth: '50px' }}
          />
        )}
        {/* Entity Selector Dropdown */}
        {renderEntitySelector && renderEntitySelector()}      
        <div className={cn(
          "min-w-0 truncate text-lg font-bold transition-colors",
          "text-white hover:text-blue-400"
        )}>          
          {entityName}
        </div> 
      </div>

      {/* Right-aligned navigation links and profile */}
      {/* Main Navigation for larger screens */}
      <nav className="hidden md:flex md:items-center md:space-x-6 ml-auto">
        
        {isAdmin(userRole) && (
          <>
            <Link to="/vehicles" className="text-white hover:underline hover:text-blue-400 transition-colors">{t('Vehicles')}</Link>
            <Link to="/gps-devices" className="text-white hover:underline hover:text-blue-400 transition-colors">{t('gpsDevices.title')}</Link>
            <Link to="/routes" className="text-white hover:underline hover:text-blue-400 transition-colors">{t('Routes')}</Link>
            <Link to="/schedule-management" className="text-white hover:underline hover:text-blue-400 transition-colors">{t('Schedules')}</Link>            
          </>
        )}                     
        
        <Link to="/bookings" className="text-white hover:underline hover:text-blue-400 transition-colors">{t('bookings')}</Link>
        {isDriver(userRole) && (
          <>
            <Link to="/operations" className="text-white hover:underline hover:text-blue-400 transition-colors">{t('operations.title')}</Link>
          </>
        )} 
        <Link to="/trips" className="text-white hover:underline hover:text-blue-400 transition-colors">{t('trips')}</Link>

        {/* Profile Dropdown */}
        <div className="relative" ref={dropdownRef}>
          <button 
            onClick={toggleProfileDropdown}
            type="button"
            className={cn(
              "flex items-center focus:outline-none transition-colors",
              "text-white hover:underline hover:text-blue-400"
            )}
          >
            <Users className="w-4 h-4 mr-1" />
            {username || t('profile')}
            <ChevronDown size={15} className="ml-1" />
          </button>
          
          {isProfileDropdownOpen && (
            <div className={cn(
              "absolute right-0 mt-2 w-64 rounded-md shadow-lg z-30 max-h-[90vh] overflow-y-auto",
              components.dropdown.base
            )}>
              <div className="py-1">
                {/* My Account Section */}
                <div className={cn("px-4 py-2 text-xs font-semibold uppercase tracking-wider", text.muted)}>
                  {t('myAccount')}
                </div>
                <Link 
                  to="/settings/account" 
                  className={cn(
                    "block px-4 py-2 flex items-center justify-between transition-colors",
                    components.dropdown.item
                  )}
                  onClick={() => setProfileDropdownOpen(false)}
                >
                  <span className="flex items-center">
                    <User className="w-4 h-4 mr-2" />
                    {t('profileInformation')}
                  </span>
                  <ChevronRight className={cn("w-4 h-4", text.muted)} />
                </Link>
                <Link 
                  to="/settings/security" 
                  className={cn(
                    "block px-4 py-2 flex items-center justify-between transition-colors",
                    components.dropdown.item
                  )}
                  onClick={() => setProfileDropdownOpen(false)}
                >
                  <span className="flex items-center">
                    <Shield className="w-4 h-4 mr-2" />
                    {t('securitySettings')}
                  </span>
                  <ChevronRight className={cn("w-4 h-4", text.muted)} />
                </Link>
                
                {/* Preferences Section */}
                <div className={cn("border-t my-2", border.default)}></div>
                <div className={cn("px-4 py-2 text-xs font-semibold uppercase tracking-wider", text.muted)}>
                  {t('preferences')}
                </div>
                <Link 
                  to="/settings/preferences" 
                  className={cn(
                    "block px-4 py-2 flex items-center justify-between transition-colors",
                    components.dropdown.item
                  )}
                  onClick={() => setProfileDropdownOpen(false)}
                >
                  <span className="flex items-center">
                    <Settings className="w-4 h-4 mr-2" />
                    {t('displaySettings')}
                  </span>
                  <ChevronRight className={cn("w-4 h-4", text.muted)} />
                </Link>
                {showNotificationMenu && (
                  <Link
                    to="/settings/notifications"
                    className={cn(
                      "block px-4 py-2 flex items-center justify-between transition-colors",
                      components.dropdown.item
                    )}
                    onClick={() => setProfileDropdownOpen(false)}
                  >
                    <span className="flex items-center">
                      <Bell className="w-4 h-4 mr-2" />
                      {t('notifications')}
                    </span>
                    <ChevronRight className={cn("w-4 h-4", text.muted)} />
                  </Link>
                )}
                {showUserSurveyMenu && (
                  <Link
                    to="/settings/feedback-surveys"
                    className={cn(
                      "block px-4 py-2 flex items-center justify-between transition-colors",
                      components.dropdown.item
                    )}
                    onClick={handleSurveyClick}
                  >
                    <span className="flex items-center relative">
                      <MessageSquare className="w-4 h-4 mr-2" />
                      {t('feedbackSurveys')}
                      {showSurveyBadge && (
                        <span className="absolute -top-1 -right-1 flex h-3 w-3">
                          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75"></span>
                          <span className="relative inline-flex rounded-full h-3 w-3 bg-blue-500"></span>
                        </span>
                      )}
                    </span>
                    <ChevronRight className={cn("w-4 h-4", text.muted)} />
                  </Link>
                )}

                {/* Admin Section */}
                {isAdmin(userRole) && (
                  <>
                    <div className={cn("border-t my-2", border.default)}></div>
                    <div className={cn("px-4 py-2 text-xs font-semibold uppercase tracking-wider", text.muted)}>
                      {t('administration')}
                    </div>
                    <Link
                      to="/settings/organization"
                      className={cn(
                        "block px-4 py-2 flex items-center justify-between transition-colors",
                        components.dropdown.item
                      )}
                      onClick={() => setProfileDropdownOpen(false)}
                    >
                      <span className="flex items-center">
                        <Building className="w-4 h-4 mr-2" />
                        {t('organizationSettings')}
                      </span>
                      <ChevronRight className={cn("w-4 h-4", text.muted)} />
                    </Link>
                    {showSurveyMenu && (
                      <Link
                        to="/settings/surveys"
                        className={cn(
                          "block px-4 py-2 flex items-center justify-between transition-colors",
                          components.dropdown.item
                        )}
                        onClick={() => setProfileDropdownOpen(false)}
                      >
                        <span className="flex items-center">
                          <MessageSquare className="w-4 h-4 mr-2" />
                          {t('surveyManagement')}
                        </span>
                        <ChevronRight className={cn("w-4 h-4", text.muted)} />
                      </Link>
                    )}
                    {showNotificationManagementMenu && (
                      <Link
                        to="/settings/notification-management"
                        className={cn(
                          "block px-4 py-2 flex items-center justify-between transition-colors",
                          components.dropdown.item
                        )}
                        onClick={() => setProfileDropdownOpen(false)}
                      >
                        <span className="flex items-center">
                          <Bell className="w-4 h-4 mr-2" />
                          {t('notificationManagement')}
                        </span>
                        <ChevronRight className={cn("w-4 h-4", text.muted)} />
                      </Link>
                    )}
                  </>
                )}

                {/* User Management / Invite Users Section - Available to all users */}
                {userRole && (
                  <>
                    {!isAdmin(userRole) && <div className={cn("border-t my-2", border.default)}></div>}
                    <Link 
                      to="/settings/users" 
                      className={cn(
                        "block px-4 py-2 flex items-center justify-between transition-colors",
                        components.dropdown.item
                      )}
                      onClick={() => setProfileDropdownOpen(false)}
                    >
                      <span className="flex items-center">
                        <UsersIcon className="w-4 h-4 mr-2" />
                        {isAdmin(userRole) 
                          ? t('userManagement') 
                          : t('inviteUsers')}
                      </span>
                      <ChevronRight className={cn("w-4 h-4", text.muted)} />
                    </Link>
                  </>
                )}

                {/* System Administration Section for super_admin and above */}
                {isSystemAdmin(userRole) && (
                  <>
                    <div className={cn("border-t my-2", border.default)}></div>
                    <div className={cn("px-4 py-2 text-xs font-semibold uppercase tracking-wider", text.muted)}>
                      {t('systemAdministration')}
                    </div>
                    <Link 
                      to="/settings/system" 
                      className={cn(
                        "block px-4 py-2 flex items-center justify-between transition-colors",
                        components.dropdown.item
                      )}
                      onClick={() => setProfileDropdownOpen(false)}
                    >
                      <span className="flex items-center">
                        <Database className="w-4 h-4 mr-2" />
                        {t('systemAdministration')}
                      </span>
                      <ChevronRight className={cn("w-4 h-4", text.muted)} />
                    </Link>
                  </>
                )}
                
                {/* Separator */}
                <div className={cn("border-t my-2", border.default)}></div>
                <button 
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleLogout();
                  }}
                  className={cn(
                    "block w-full text-left px-4 py-2 transition-colors",
                    components.dropdown.item
                  )}
                >
                  <div className="flex items-center gap-2">
                    <LucideLogOut className="w-4 h-4" /> 
                    {t('logout')}
                  </div>                  
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Language Selector */}
        <div className={cn(
          "ml-4 border rounded-md transition-colors",
          "text-white hover:text-blue-400",
          "border-white hover:border-blue-400"
        )}>
          <LanguageSelector />
        </div>
      </nav>
      <div className="ml-3 shrink-0"><AnnouncementBell /></div>
    </header>
    <MobileBottomNavigation
      userRole={userRole}
      showNotificationMenu={showNotificationMenu}
      showNotificationManagementMenu={showNotificationManagementMenu}
      showSurveyMenu={showSurveyMenu}
      showUserSurveyMenu={showUserSurveyMenu}
      showSurveyBadge={showSurveyBadge}
      onLogout={(event) => handleLogout(event, 'user_logout')}
      onSurveyClick={handleSurveyClick}
    />
    </>
  );
}

export default Header;
